/**
 * Public location display for event cards and the event detail page.
 *
 * Prefer structured `event.Address` (manual programs, address overrides).
 * Fall back to parsing `event.Location` for typical scraped rows.
 */

export function parseLocation(location) {
  if (!location || location === 'TBD') {
    return { venue: 'TBD', address: null };
  }

  const parts = location.split(/[•,]/).map((p) => p.trim()).filter(Boolean);

  if (parts.length >= 2) {
    return {
      venue: parts[0],
      address: parts.slice(1).join(', '),
    };
  }

  if (/\d/.test(location) || location.toLowerCase().includes('st') || location.toLowerCase().includes('ave')) {
    return { venue: null, address: location };
  }

  return { venue: location, address: null };
}

export function extractCityFromLocation(locationStr = '') {
  const parts = String(locationStr || '').split(',');
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (part === 'CA' || part.endsWith('CA') || part.startsWith('CA') || /\bCA\b/.test(part)) {
      if (i > 0) return parts[i - 1].trim();
    }
  }
  return parts[0].trim() || null;
}

function escapeRegex(s = '') {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function isCityStateOnlyLocation(fullLocation = '', city = '') {
  const full = (fullLocation || '').trim().replace(/\s+/g, ' ');
  const c = (city || '').trim().replace(/\s+/g, ' ');
  if (!full || !c) return false;
  if (full.localeCompare(c, undefined, { sensitivity: 'accent' }) === 0) return true;
  const re = new RegExp(`^${escapeRegex(c)}\\s*,\\s*[A-Za-z]{2}(?:\\s+\\d{5}(?:-\\d{4})?)?$`, 'i');
  return re.test(full);
}

function trimOrNull(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

export function formatStreetAddress(addr) {
  if (!addr || typeof addr !== 'object') return '';
  const stateZip = [addr.state, addr.postalCode].filter(Boolean).join(' ');
  return [addr.addressLine1, addr.addressLine2, addr.city, stateZip]
    .map((part) => trimOrNull(part))
    .filter(Boolean)
    .join(', ');
}

export function formatVenueLocationFromAddress(addr) {
  if (!addr || typeof addr !== 'object') return '';
  const venue = trimOrNull(addr.venue);
  const street = formatStreetAddress(addr);
  return [venue, street].filter(Boolean).join(', ');
}

export function cityFromEvent(event) {
  const fromAddress = trimOrNull(event?.Address?.city);
  if (fromAddress) return fromAddress;
  return extractCityFromLocation(event?.Location);
}

export function resolveEventLocation(event) {
  const location = event?.Location || '';
  const structured = event?.Address && typeof event.Address === 'object' ? event.Address : null;
  const isTbd = (!location || location === 'TBD') && !trimOrNull(structured?.venue) && !trimOrNull(structured?.addressLine1);

  if (isTbd) {
    return {
      isTbd: true,
      city: null,
      venue: 'TBD',
      address: null,
      mapsQuery: '',
      cardPrimary: null,
      cardSecondary: null,
      showSecondary: false,
    };
  }

  if (structured) {
    const venue = trimOrNull(structured.venue);
    const address = formatStreetAddress(structured) || null;
    const city = trimOrNull(structured.city) || extractCityFromLocation(location);
    const composed = formatVenueLocationFromAddress(structured);
    const mapsQuery =
      trimOrNull(structured.mapsQuery) ||
      trimOrNull(structured.formattedAddress) ||
      composed ||
      location;
    const hasStreet = Boolean(trimOrNull(structured.addressLine1));
    const cardSecondary = composed || location;
    const showSecondary = hasStreet || (Boolean(city) && !isCityStateOnlyLocation(cardSecondary, city));

    return {
      isTbd: false,
      city,
      venue: venue || parseLocation(location).venue,
      address,
      mapsQuery,
      cardPrimary: city || cardSecondary,
      cardSecondary,
      showSecondary,
    };
  }

  const parsed = parseLocation(location);
  const city = extractCityFromLocation(location);
  const showSecondary = Boolean(city) && !isCityStateOnlyLocation(location, city);

  return {
    isTbd: false,
    city,
    venue: parsed.venue,
    address: parsed.address,
    mapsQuery: location,
    cardPrimary: city || location,
    cardSecondary: location,
    showSecondary,
  };
}
